import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { AccountFaqCategory, SEED_FAQ_CATEGORIES } from '../../models/faq';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';

/**
 * Website FAQs → Categories (#202). Each category is a record with a name, an
 * optional intro paragraph and a display position; FAQ items reference one by
 * id, and a FAQ block set to group by category renders a section per category
 * in this order.
 *
 * Replaces a fixed list that lived in this repo AND in picaluna-v3's faq-hub —
 * adding one meant editing both, and a value missing from v3's order array
 * rendered nothing.
 *
 * Edited as a whole list rather than per-row dialogs: there are a handful of
 * them, each is three fields, and reordering is the main thing anyone comes
 * here to do.
 */
@Component({
  selector: 'app-account-faq-categories',
  templateUrl: './account-faq-categories.component.html',
  styleUrls: ['./account-faq-categories.component.scss'],
})
export class AccountFaqCategoriesComponent implements OnInit, OnDestroy {
  rows: AccountFaqCategory[] = [];
  loading = true;
  saving = false;

  /** Ids present when the dialog opened — anything missing on save is removed. */
  private initialIds = new Set<string>();
  private unsubscribe$ = new Subject<void>();

  constructor(
    public ref: DynamicDialogRef,
    private accountService: AccountService,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.accountService
      .ensureFullAccount()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => this.load(),
        error: () => (this.loading = false),
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private load(): void {
    this.accountService.account$.pipe(first()).subscribe({
      next: (a: any) => {
        const raw = a?.data?.content?.faqCategories;
        const list: AccountFaqCategory[] = (
          Array.isArray(raw) ? raw : Object.values(raw || {})
        ).filter((c: any) => c && typeof c === 'object' && !c?._meta?.deleted);

        // First open on an account that predates categories: offer the set the
        // old fixed list defined, ids matching the slugs already stored on the
        // FAQ records so nothing needs migrating. Not written until Save.
        this.rows = list.length
          ? list.slice().sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
          : SEED_FAQ_CATEGORIES.map((c, i) => ({
              _meta: { id: c.id, index: i },
              name: c.name,
              order: c.order,
            }));

        this.initialIds = new Set(list.map((c) => c._meta.id));
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  addRow(): void {
    const order = this.rows.length ? Math.max(...this.rows.map((r) => r.order ?? 0)) + 10 : 10;
    this.rows.push({
      _meta: { id: `id-faqcat-${Math.random().toString(36).slice(2, 10)}`, index: this.rows.length },
      name: '',
      order,
    });
  }

  removeRow(i: number): void {
    this.rows.splice(i, 1);
  }

  move(i: number, delta: number): void {
    const j = i + delta;
    if (j < 0 || j >= this.rows.length) return;
    const [row] = this.rows.splice(i, 1);
    this.rows.splice(j, 0, row);
    // Positions follow the visible order, so the numbers never disagree with
    // what the list shows.
    this.rows.forEach((r, k) => (r.order = (k + 1) * 10));
  }

  save(): void {
    const kept = this.rows
      .map((r, i) => ({ ...r, name: (r.name || '').trim(), order: r.order ?? (i + 1) * 10 }))
      .filter((r) => r.name);

    if (!kept.length) {
      this.modalService.generic({
        title: 'Add at least one category',
        copy: ['A category needs a name — an unnamed one has nothing to show above its questions.'],
        buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
      });
      return;
    }

    const keptIds = new Set(kept.map((r) => r._meta.id));
    const removed = [...this.initialIds].filter((id) => !keptIds.has(id));

    this.saving = true;

    /* ONE request for the whole dialog (#225). The server applies upsert[] and
       remove[] per item, each to its own subtree, so a batch is exactly as
       concurrency-safe as the sequence it replaces — and it does not get
       slower with each category added. It was 2 round trips per row (a write
       plus the account refetch every write performs), so 12 categories cost
       24; it is now 2 regardless.

       Parallelising the old per-item calls would NOT have been safe: each
       resolves with a full account fetch and the last response wins, so
       overlapping them can resurrect a stale subtree. */
    this.accountService
      .patchContentItems('faqCategories', kept, removed)
      .pipe(first())
      .subscribe({
        next: () => {
          this.saving = false;
          this.ref.close({ saved: true });
        },
        error: (err) => {
          this.saving = false;
          this.modalService.error(err?.error?.error || err);
        },
      });
  }

  cancel(): void {
    this.ref.close(undefined);
  }
}
